import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, Search, X, Loader2, ChevronDown, ChevronRight,
  UserCheck, Baby, Download,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';

export default function ParentsPage() {
  const navigate = useNavigate();
  const [parents, setParents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState({ total: 0, male: 0, female: 0, linked_students: 0 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/school/parents?search=${encodeURIComponent(search)}`);
      setParents(res.data || []);
    } catch { toast.error('Failed to load parents'); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/school/parents/stats').then(r => { if (r.success) setStats(r.data); }).catch(() => {});
  }, []);

  const handleExportCSV = () => {
    const headers = ['first_name', 'last_name', 'email', 'phone', 'children_count'];
    const rows = parents.map(p => headers.map(h => `"${(p[h] ?? p.child_count ?? '').toString().replace(/"/g, '""')}"`).join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const a = document.createElement('a');
    a.href = `data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`;
    a.download = 'parents.csv';
    a.click();
  };

  return (
    <div className="p-4 lg:p-6 max-w-7xl mx-auto space-y-5" data-testid="parents-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Parents / Guardians</h1>
          <p className="text-sm text-slate-500 mt-0.5">{stats.total} registered parents</p>
        </div>
        <button onClick={handleExportCSV}
          className="flex items-center gap-1.5 border border-slate-200 text-slate-600 text-sm font-semibold px-3 py-2 rounded-xl hover:bg-slate-50 transition-colors self-start">
          <Download className="w-4 h-4" /> Export
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total Parents', value: stats.total, icon: Users, color: 'indigo' },
          { label: 'Linked Students', value: stats.linked_students, icon: Baby, color: 'emerald' },
          { label: 'Male Guardians', value: stats.male, icon: UserCheck, color: 'blue' },
          { label: 'Female Guardians', value: stats.female, icon: UserCheck, color: 'violet' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
            <div className={`w-8 h-8 rounded-xl flex items-center justify-center mb-2 bg-${s.color}-100`}>
              <s.icon className={`w-4 h-4 text-${s.color}-600`} />
            </div>
            <p className="text-2xl font-extrabold text-slate-900">{s.value}</p>
            <p className="text-xs text-slate-500 font-medium mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or email..."
            className="w-full h-10 pl-9 pr-9 text-sm border border-slate-200 rounded-xl outline-none focus:border-indigo-400 transition-colors"
            data-testid="parent-search" />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-600">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid="parents-list">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-7 h-7 text-indigo-600 animate-spin" />
          </div>
        ) : parents.length === 0 ? (
          <div className="py-16 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-slate-400" />
            </div>
            <p className="font-bold text-slate-700">No parents found</p>
            <p className="text-sm text-slate-400 mt-1">Parents are created automatically when enrolling students</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/60">
                  <th className="text-left px-5 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide">Parent</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide hidden sm:table-cell">Email</th>
                  <th className="text-center px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide">Children</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide hidden md:table-cell">Phone</th>
                  <th className="px-4 py-3 text-right text-xs font-bold text-slate-500 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody>
                {parents.map(p => (
                  <tr key={p.id}
                    onClick={() => navigate(`/school/parents/${p.id}`)}
                    className="border-b border-slate-50 hover:bg-indigo-50/30 cursor-pointer transition-colors group"
                    data-testid={`parent-row-${p.id}`}>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-violet-400 to-indigo-500 flex items-center justify-center flex-shrink-0 text-white text-xs font-extrabold">
                          {p.first_name?.[0]}{p.last_name?.[0]}
                        </div>
                        <div>
                          <p className="text-sm font-bold text-slate-800 group-hover:text-indigo-700 transition-colors">{p.first_name} {p.last_name}</p>
                          <p className="text-xs text-slate-400 sm:hidden">{p.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-sm text-slate-500 hidden sm:table-cell">{p.email}</td>
                    <td className="px-4 py-3.5 text-center">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 text-xs font-extrabold">
                        {p.child_count || 0}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-sm text-slate-500 hidden md:table-cell">{p.phone || '—'}</td>
                    <td className="px-4 py-3.5 text-right">
                      <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-indigo-400 transition-colors ml-auto" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-5 py-3 border-t border-slate-50 text-xs text-slate-400">
              Showing {parents.length} parent{parents.length !== 1 ? 's' : ''}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
